import React, { useState } from 'react';
import { X, Plus, Trash2, Save } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from '../lib/toast';

interface DailyPlannerSettingsProps {
  onClose: () => void;
  onStatusesUpdated: () => void;
  initialStatuses: string[];
}

export default function DailyPlannerSettings({ onClose, onStatusesUpdated, initialStatuses }: DailyPlannerSettingsProps) {
  const [statuses, setStatuses] = useState<string[]>(initialStatuses);
  const [newStatus, setNewStatus] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAddStatus = () => {
    if (!newStatus.trim()) {
      toast.error('Please enter a status');
      return;
    }

    if (statuses.includes(newStatus.trim())) {
      toast.error('This status already exists');
      return;
    }

    setStatuses([...statuses, newStatus.trim()]);
    setNewStatus('');
  };

  const handleRemoveStatus = (index: number) => {
    setStatuses(statuses.filter((_, i) => i !== index));
  };

  const handleSaveStatuses = async () => {
    if (statuses.length === 0) {
      toast.error('You must have at least one status');
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Delete existing statuses
      const { error: deleteError } = await supabase
        .from('daily_planner_statuses')
        .delete()
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      // Insert new statuses
      const { error: insertError } = await supabase
        .from('daily_planner_statuses')
        .upsert(
          statuses.map(status => ({
            status,
            user_id: user.id
          })),
          { onConflict: 'user_id,status' }
        );

      if (insertError) throw insertError;

      toast.success('Statuses saved successfully');
      onStatusesUpdated();
      onClose();
    } catch (error) {
      console.error('Error saving statuses:', error);
      toast.error('Failed to save statuses');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Manage Task Statuses
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex space-x-2">
              <input
                type="text"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                placeholder="Enter new status"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <button
                onClick={handleAddStatus}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add
              </button>
            </div>

            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Statuses</h3>
              {statuses.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No statuses defined yet.</p>
              ) : (
                <ul className="space-y-2">
                  {statuses.map((status, index) => (
                    <li key={index} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded-md">
                      <span className="text-gray-700 dark:text-gray-300">{status}</span>
                      <button
                        onClick={() => handleRemoveStatus(index)}
                        className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveStatuses}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {saving ? (
                <>Saving...</>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Save Statuses
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}